import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, input, signal, viewChild} from '@angular/core';
import {DecimalPipe, PercentPipe} from '@angular/common';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCardModule} from '@angular/material/card';
import * as echarts from 'echarts';
import {County} from '../../../core/classes/county';
import {AverageCurve} from '../../../services/edgar-pipeline.service';
import {
  CURVE_SAMPLE_COUNT,
  DEFAULT_IMPACT_DURATION_MINUTES,
  DEFAULT_RECOVERY_DURATION_MINUTES,
  piecewiseTau,
} from '../../utilities/outage-curve';

type CurvePopupData = County & {curveData: AverageCurve | null};

@Component({
  selector: 'tgrid-county-src-popup',
  imports: [DecimalPipe, PercentPipe, MatButtonToggleModule, MatCardModule],
  templateUrl: './county-src-popup.component.html',
  styleUrl: './county-src-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountySrcPopupComponent {
  public readonly data = input.required<CurvePopupData>();
  protected readonly normalized = signal(true);
  protected readonly chartElement = viewChild<ElementRef<HTMLDivElement>>('chart');
  protected readonly curve = computed(() => this.data().curveData);
  protected readonly title = computed(() => `Average Power Outage Curve — ${this.data().name}, ${this.data().stateAbbreviation}`);
  private chart?: echarts.ECharts;

  public constructor() {
    effect(() => {
      const element = this.chartElement()?.nativeElement;
      const curve = this.curve();
      const normalized = this.normalized();
      if (!element || !curve) return;
      this.chart ??= echarts.init(element);
      this.chart.setOption(this.chartOptions(curve, normalized), true);
    });
  }

  private chartOptions(curve: AverageCurve, normalized: boolean): echarts.EChartsOption {
    const impactMinutes = Math.max(1, curve.avgImpactDuration ?? DEFAULT_IMPACT_DURATION_MINUTES);
    const recoveryMinutes = Math.max(1, curve.avgRecoveryDuration ?? DEFAULT_RECOVERY_DURATION_MINUTES);
    const riseTau = curve.avgTauRise ?? 0;
    const fallTau = curve.avgTauFall ?? 0;
    const normalizedPeak = curve.impactLevelNormalized ?? curve.avgImpactLevelNormalized ?? 0;
    const countySize = Math.max(curve.countySize ?? 1, 1);
    const peak = normalized ? normalizedPeak : curve.impactLevel ?? normalizedPeak * countySize;
    const threshold = normalized
      ? (curve.threshold ?? 0) / countySize
      : curve.threshold ?? 0;
    const points = Array.from({length: CURVE_SAMPLE_COUNT}, (_unused, index) => {
      const minute = -impactMinutes + (impactMinutes + recoveryMinutes) * index / (CURVE_SAMPLE_COUNT - 1);
      return [minute, piecewiseTau(minute, -impactMinutes, threshold, 0, peak, recoveryMinutes, threshold, riseTau, fallTau)] as [number, number];
    });
    const riseStd = Math.max(0, curve.stdTauRise ?? 0);
    const fallStd = Math.max(0, curve.stdTauFall ?? 0);
    const varied = [-riseStd, 0, riseStd].flatMap(riseOffset =>
      [-fallStd, 0, fallStd].map(fallOffset =>
        points.map(([minute]) => piecewiseTau(
          minute, -impactMinutes, threshold, 0, peak, recoveryMinutes, threshold,
          riseTau + riseOffset, fallTau + fallOffset,
        )),
      ),
    );
    const bandMinimum = points.map(([minute], index) => [minute, Math.min(...varied.map(values => values[index]))] as [number, number]);
    const bandMaximum = points.map(([minute], index) => [minute, Math.max(...varied.map(values => values[index]))] as [number, number]);
    const hasTauBand = riseStd > 0 || fallStd > 0;
    const tauBandSeries: echarts.CustomSeriesOption[] = hasTauBand ? [{
      type: 'custom',
      name: 'Tau uncertainty',
      silent: true,
      z: 1,
      data: [0],
      renderItem: (_params, api) => ({
        type: 'polygon',
        shape: {
          points: [
            ...bandMaximum.map(point => api.coord(point)),
            ...[...bandMinimum].reverse().map(point => api.coord(point)),
          ],
        },
        style: {fill: '#9ecae1', opacity: .2},
      }),
    }] : [];
    const impactDurationBounds = [curve.ciImpactDurationMin, curve.ciImpactDurationMax]
      .filter((value): value is number => value != null).map(value => -value);
    const recoveryDurationBounds = [curve.ciRecoveryDurationMin, curve.ciRecoveryDurationMax]
      .filter((value): value is number => value != null);
    const impactLevelBounds = [curve.ciImpactLevelMin, curve.ciImpactLevelMax]
      .filter((value): value is number => value != null).map(value => normalized ? value : value * countySize);
    const markAreas: object[] = [];
    if (impactDurationBounds.length === 2) markAreas.push([
      {name: 'Impact duration CI', xAxis: Math.min(...impactDurationBounds), itemStyle: {color: 'rgba(244, 201, 125, .14)', borderColor: '#d18d21', borderWidth: 1}},
      {xAxis: Math.max(...impactDurationBounds)},
    ]);
    if (recoveryDurationBounds.length === 2) markAreas.push([
      {name: 'Recovery duration CI', xAxis: Math.min(...recoveryDurationBounds), itemStyle: {color: 'rgba(139, 201, 143, .14)', borderColor: '#3a8a46', borderWidth: 1}},
      {xAxis: Math.max(...recoveryDurationBounds)},
    ]);
    if (impactLevelBounds.length === 2) markAreas.push([
      {name: 'Impact level CI', yAxis: Math.min(...impactLevelBounds), itemStyle: {color: 'rgba(255, 107, 107, .10)', borderColor: '#cf3f3f', borderWidth: 1}},
      {yAxis: Math.max(...impactLevelBounds)},
    ]);
    return {
      animation: false,
      grid: {top: 24, right: 24, bottom: 50, left: 66},
      tooltip: {trigger: 'axis', valueFormatter: value => Number(value).toLocaleString(undefined, {maximumFractionDigits: 2})},
      xAxis: {type: 'value', name: 'Time (min)', nameLocation: 'middle', nameGap: 32},
      yAxis: {
        type: 'value',
        min: 0,
        name: normalized ? 'Outage Rate' : 'Customers Affected',
        axisLabel: {formatter: normalized ? (value: number) => value.toFixed(3) : (value: number) => value.toLocaleString()},
      },
      series: [
        ...tauBandSeries,
        {
          type: 'line', name: 'Average outage curve', data: points, showSymbol: false,
          lineStyle: {color: '#0b39ff', width: 2.5},
          markArea: {silent: true, label: {show: false}, data: markAreas as never},
          markLine: {
            silent: true, symbol: 'none', label: {show: false},
            data: [
              {xAxis: 0, lineStyle: {color: '#94a3b8', type: 'dashed', width: 1}},
              [{coord: [-impactMinutes, threshold]}, {coord: [recoveryMinutes, threshold], lineStyle: {color: '#ef4444', type: 'dashed', width: 1.5}}],
            ] as never,
          },
        },
      ],
    };
  }

}
